import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';

// XP Boot Screen — shows loading bar, then "Welcome" screen, then desktop
// Plays the Windows XP startup sound when transitioning to desktop

const BOOT_PHASE = {
  LOADING: 'loading',    // Black screen with blue loading bar
  WELCOME: 'welcome',    // Blue "Welcome" screen
  DESKTOP: 'desktop',    // Fade into desktop
};

const BOOT_DURATION = 2000;    // Loading bar phase (ms)
const WELCOME_DURATION = 1000;  // Welcome screen (ms)
const FADE_DURATION = 500;      // Fade out (ms)

// Windows XP startup sound — base64 encoded short chime
// Using a URL to a well-known XP sound instead
const XP_STARTUP_SOUND = 'https://archive.org/download/WindowsXpStartup_201901/Windows%20XP%20Startup.mp3';

function BootScreen({ children }) {
  const [phase, setPhase] = useState(BOOT_PHASE.LOADING);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    // Phase 1: Loading bar
    const welcomeTimer = setTimeout(() => {
      setPhase(BOOT_PHASE.WELCOME);
      // Play startup sound
      try {
        const audio = new Audio(XP_STARTUP_SOUND);
        audio.volume = 0.3;
        audio.play().catch(() => { }); // Silently fail if autoplay blocked
      } catch (e) { }
    }, BOOT_DURATION);

    // Phase 2: Welcome → Desktop
    const desktopTimer = setTimeout(() => {
      setPhase(BOOT_PHASE.DESKTOP);
    }, BOOT_DURATION + WELCOME_DURATION);

    // Phase 3: Remove overlay
    const hideTimer = setTimeout(() => {
      setVisible(false);
    }, BOOT_DURATION + WELCOME_DURATION + FADE_DURATION);

    return () => {
      clearTimeout(welcomeTimer);
      clearTimeout(desktopTimer);
      clearTimeout(hideTimer);
    };
  }, []);

  // Allow clicking to skip boot
  const skipBoot = useCallback(() => {
    setPhase(BOOT_PHASE.DESKTOP);
    setTimeout(() => setVisible(false), 300);
  }, []);

  return (
    <>
      {children}
      {visible && (
        <Overlay phase={phase} onClick={skipBoot}>
          {phase === BOOT_PHASE.LOADING && (
            <LoadingScreen>
              <XPLogo>
                <LogoFlag>
                  <FlagPiece color="#f44336" />
                  <FlagPiece color="#4CAF50" />
                  <FlagPiece color="#2196F3" />
                  <FlagPiece color="#FFEB3B" />
                </LogoFlag>
                <LogoText>
                  Microsoft<Reg>®</Reg> Windows<Sub>®</Sub>
                  <Edition>XP</Edition>
                </LogoText>
              </XPLogo>
              <LoadingBarContainer>
                <LoadingBarTrack>
                  <LoadingBlock style={{ animationDelay: '0s' }} />
                  <LoadingBlock style={{ animationDelay: '0.15s' }} />
                  <LoadingBlock style={{ animationDelay: '0.3s' }} />
                </LoadingBarTrack>
              </LoadingBarContainer>
              <SkipHint>Click anywhere to skip</SkipHint>
            </LoadingScreen>
          )}
          {phase === BOOT_PHASE.WELCOME && (
            <WelcomeScreen>
              <WelcomeText>Welcome</WelcomeText>
            </WelcomeScreen>
          )}
        </Overlay>
      )}
    </>
  );
}

// Animations
const slideBlocks = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(400%); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

const welcomePulse = keyframes`
  0%, 100% { opacity: 0.7; }
  50% { opacity: 1; }
`;

// Styled Components
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 999999;
  cursor: pointer;
  animation: ${({ phase }) =>
    phase === 'desktop' ? fadeOut : 'none'} 0.8s forwards;
`;

const LoadingScreen = styled.div`
  width: 100%;
  height: 100%;
  background: #000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  animation: ${fadeIn} 0.5s ease;
`;

const XPLogo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 60px;
`;

const LogoFlag = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  width: 44px;
  height: 44px;
  gap: 2px;
  transform: rotate(-10deg) skewX(-5deg);
`;

const FlagPiece = styled.div`
  background: ${({ color }) => color};
  border-radius: 2px;
`;

const LogoText = styled.div`
  color: #fff;
  font-family: 'Segoe UI', 'Franklin Gothic Medium', Tahoma, sans-serif;
  font-size: 26px;
  font-weight: 300;
  letter-spacing: 3px;
`;

const Reg = styled.span`
  font-size: 10px;
  vertical-align: super;
`;

const Sub = styled.span`
  font-size: 10px;
  vertical-align: super;
  margin-right: 4px;
`;

const Edition = styled.span`
  font-weight: 700;
  color: #fff;
  font-style: italic;
  margin-left: 4px;
`;

const LoadingBarContainer = styled.div`
  width: 220px;
  height: 22px;
  border: 1px solid #333;
  border-radius: 12px;
  overflow: hidden;
  background: #111;
  padding: 3px;
`;

const LoadingBarTrack = styled.div`
  display: flex;
  gap: 3px;
  height: 100%;
  overflow: hidden;
  border-radius: 8px;
`;

const LoadingBlock = styled.div`
  width: 12px;
  height: 100%;
  background: linear-gradient(180deg, #5599ff 0%, #2266cc 50%, #1144aa 100%);
  border-radius: 3px;
  flex-shrink: 0;
  animation: ${slideBlocks} 1.5s ease-in-out infinite;
`;

const SkipHint = styled.div`
  position: absolute;
  bottom: 40px;
  color: #444;
  font-size: 11px;
  font-family: Tahoma, sans-serif;
`;

const WelcomeScreen = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(180deg, #0052cc 0%, #003d99 30%, #002b6b 70%, #001a40 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${fadeIn} 0.5s ease;
`;

const WelcomeText = styled.div`
  color: #fff;
  font-family: 'Segoe UI', 'Franklin Gothic Medium', Tahoma, sans-serif;
  font-size: 48px;
  font-weight: 700;
  font-style: italic;
  letter-spacing: 2px;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
  animation: ${welcomePulse} 2s ease-in-out infinite;
`;

export default BootScreen;
